import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import { API_BASE, api } from '../lib/api'
import { useTabParam } from '../hooks/useTabParam'
import DateRangePicker from '../components/dashboard/DateRangePicker'
import '../styles/ui.css'
import '../styles/roadmap.css'
import '../styles/loyalty-audit.css'

// ── API types ─────────────────────────────────────────────────────────

type AbSignal = { key: string; label: string; description: string }

type AbStoreOption = { id: string; storeNumber: string | null; name: string; isClosed: boolean }

type AbConfig = {
    stores: AbStoreOption[]
    storesFromLegacy: boolean
    canViewChainWide: boolean
    canViewUnassigned: boolean
    hasAssignments: boolean
    signals: AbSignal[]
    defaults: {
        windowDays: number
        from: string
        to: string
        perEntryThreshold: number
        dailyThreshold: number
        selfCreditThreshold: number
        retroDaysThreshold: number
    }
}

type AbMeta = {
    from: string | null
    to: string | null
    allHistory: boolean
    storeSelection: string
    perEntryThreshold: number
    dailyThreshold: number
    selfCreditThreshold: number
    retroDaysThreshold: number
    builtAt: string
    buildMs: number
}

type AbOverview = {
    meta: AbMeta
    totals: {
        flaggedEntries: number; customers: number; totalCredited: number; employees: number
        autoRegEntries: number; autoRegCredited: number; employeeDays: number
    }
    signalCounts: Record<string, number>
    stores: {
        storeId: string | null; storeNumber: string | null; storeName: string
        flaggedEntries: number; customers: number; totalCredited: number
        autoRegEntries: number; autoRegCredited: number
        firstDate: string | null; lastDate: string | null
    }[]
}

type AbEntry = {
    entryId: string
    storeId: string | null; storeNumber: string | null; storeName: string
    date: string; creditedAt: string
    loyaltyNumber: string | null; customerName: string; email: string | null; customerId: string | null
    employeeId: string | null; appliedBy: string
    logType: string; noteClass: string
    entryCredited: number; entryNet: number; itemLines: number
    dayCredited: number; dayRedeemed: number; dayManualCreditEntries: number; dayStoreCount: number
    saleTime: string | null; saleSubTotal: number | null
    eStoreOrderNumber: number | null; retroDays: number | null; maxEarnRate: number | null
    signals: string[]; notes: string[]; noteText: string | null
    relatedTransactionId: string | null
}

type AbEntries = { meta: AbMeta; total: number; page: number; pageSize: number; rows: AbEntry[] }

type AbEmployeeDay = {
    date: string; manualCredited: number; customersCredited: number
    manualRetroEntries: number; adHocEntries: number; orphanEntries: number
    selfCreditEntries: number; autoRegCredited: number
}

type AbEmployee = {
    storeId: string | null; storeNumber: string | null; storeName: string
    employeeId: string | null; employeeName: string; departed: boolean
    flaggedEntries: number; customers: number; totalCredited: number
    manualRetroEntries: number; autoRegEntries: number; adHocEntries: number; selfCreditEntries: number
    firstDate: string | null; lastDate: string | null
    monitorDayCount: number; monitorCredited: number; monitorOrphans: number
    monitorDays: AbEmployeeDay[]
}

type AbEmployees = { meta: AbMeta; total: number; rows: AbEmployee[] }

type AbAutoReg = {
    storeId: string | null; storeNumber: string | null; storeName: string
    date: string; loyaltyNumber: string | null; customerName: string; registeredBy: string
    entries: number; totalCredited: number; burstDaysForCustomer: number
}

type AbAutoRegs = { meta: AbMeta; total: number; rows: AbAutoReg[] }

// ── Presentation helpers ──────────────────────────────────────────────

/**
 * Signal severity. Red is "this should not be possible at all", amber is "this needs an
 * explanation", blue is "this is worth a glance". Same-day churn is deliberately the mildest:
 * it is by far the most common signal and is usually a customer spending what they just earned.
 */
const SEVERITY: Record<string, 'high' | 'medium' | 'low'> = {
    dupSale: 'high',
    orphan: 'high',
    selfCredit: 'high',
    retroCredit: 'medium',
    rateAnomaly: 'medium',
    adHoc: 'medium',
    entryOverThreshold: 'medium',
    dayOverThreshold: 'medium',
    sameDayChurn: 'low',
}

const SORTS: { id: string; label: string }[] = [
    { id: 'credited', label: 'Largest credit' },
    { id: 'dayCredited', label: 'Largest day total' },
    { id: 'date', label: 'Newest first' },
    { id: 'dateAsc', label: 'Oldest first' },
    { id: 'store', label: 'Store' },
    { id: 'employee', label: 'Employee' },
]

// en-CA to match the dashboards (dashboardHelpers.ts) — the browser's own locale renders CAD
// as "CA$41,288.42", which reads as a foreign currency to a Canadian retailer. Cents are kept:
// this is an audit, and a $0.07 discrepancy is still a discrepancy.
const moneyFmt = new Intl.NumberFormat('en-CA', {
    style: 'currency', currency: 'CAD', minimumFractionDigits: 2, maximumFractionDigits: 2,
})
const money = (n: number | null | undefined) => (n == null ? '—' : moneyFmt.format(n))

const countFmt = new Intl.NumberFormat('en-CA')
const count = (n: number | null | undefined) => (n == null ? '—' : countFmt.format(n))

const dateTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—')

const rate = (r: number | null) => (r == null ? '—' : `${(r * 100).toFixed(2)}%`)

/**
 * The api() helper rethrows the raw response body, which for a failed audit is an RFC 9110
 * ProblemDetails document. Dumping that at a reviewer is noise; the one part that matters is
 * the sentence with the correlation id in it.
 */
function errorMessage(err: unknown): string {
    const raw = (err as Error)?.message ?? ''
    try {
        const parsed = JSON.parse(raw)
        return parsed.detail || parsed.message || parsed.error || raw
    } catch {
        return raw || 'The audit could not be loaded.'
    }
}

const TABS = ['overview', 'entries', 'employees', 'auto'] as const
type Tab = typeof TABS[number]

// ── Page ──────────────────────────────────────────────────────────────

export default function AdventureBucksAuditPage() {
    const token = useAuthStore(s => s.accessToken)
    const user = useAuthStore(s => s.user)
    const canExport = user?.permissions?.includes('reports.export.manual') ?? false
    const [tab, setTab] = useTabParam<Tab>(TABS, 'overview')
    const [params, setParams] = useSearchParams()

    const { data: config } = useQuery<AbConfig>({
        queryKey: ['ab-audit-config'],
        queryFn: () => api('/api/loyalty-audit/config', token),
        staleTime: 5 * 60_000,
    })

    // Filters live in the URL: an audit finding is something you send to someone else, and a
    // link that reopens the exact window, store and signal set is the whole point.
    const allHistory = params.get('window') === 'all'
    const from = params.get('from') ?? config?.defaults.from ?? ''
    const to = params.get('to') ?? config?.defaults.to ?? ''
    const storeId = params.get('storeId') ?? ''
    const signals = params.get('signals') ?? ''
    const perEntry = params.get('per') ?? ''
    const daily = params.get('daily') ?? ''
    const selfCredit = params.get('self') ?? ''
    const retroDays = params.get('retro') ?? ''
    const sort = params.get('sort') ?? 'credited'
    const page = Math.max(parseInt(params.get('page') ?? '1', 10) || 1, 1)
    const employeeId = params.get('employeeId') ?? ''

    const patch = useCallback((changes: Record<string, string | null>, resetPage = true) => {
        setParams(prev => {
            const next = new URLSearchParams(prev)
            for (const [k, v] of Object.entries(changes)) {
                if (v === null || v === '') next.delete(k)
                else next.set(k, v)
            }
            if (resetPage) next.delete('page')
            return next
        }, { replace: true })
    }, [setParams])

    // The query string every tab shares. Built once so the four endpoints agree on the window,
    // which is also what lets them all hit the same cached snapshot server-side.
    const query = useMemo(() => {
        const q = new URLSearchParams()
        if (!allHistory && from && to) { q.set('from', from); q.set('to', to) }
        if (storeId) q.set('storeId', storeId)
        if (signals) q.set('signals', signals)
        if (perEntry) q.set('perEntryThreshold', perEntry)
        if (daily) q.set('dailyThreshold', daily)
        if (selfCredit) q.set('selfCreditThreshold', selfCredit)
        if (retroDays) q.set('retroDaysThreshold', retroDays)
        return q.toString()
    }, [allHistory, from, to, storeId, signals, perEntry, daily, selfCredit, retroDays])

    const enabled = !!token && !!config

    const overview = useQuery<AbOverview>({
        queryKey: ['ab-audit-overview', query],
        queryFn: () => api(`/api/loyalty-audit/overview?${query}`, token),
        enabled,
    })

    const entriesQuery = new URLSearchParams(query)
    entriesQuery.set('sort', sort)
    entriesQuery.set('page', String(page))
    entriesQuery.set('pageSize', '50')
    if (employeeId) entriesQuery.set('employeeId', employeeId)

    const entries = useQuery<AbEntries>({
        queryKey: ['ab-audit-entries', entriesQuery.toString()],
        queryFn: () => api(`/api/loyalty-audit/entries?${entriesQuery}`, token),
        enabled: enabled && tab === 'entries',
    })

    const employees = useQuery<AbEmployees>({
        queryKey: ['ab-audit-employees', query],
        queryFn: () => api(`/api/loyalty-audit/employees?${query}`, token),
        enabled: enabled && tab === 'employees',
    })

    const autoRegs = useQuery<AbAutoRegs>({
        queryKey: ['ab-audit-autoreg', query],
        queryFn: () => api(`/api/loyalty-audit/auto-registrations?${query}`, token),
        enabled: enabled && tab === 'auto',
    })

    // Who the employee filter is on. The Employees tab's summary names them once it has loaded;
    // arriving straight on Entries — from the dashboard's Employee Performance widget, say — it
    // hasn't, so the filtered entries' own "applied by" names them instead. Both come from the same
    // snapshot, and ids compare without regard to case, as the server compares them. A name once
    // found is kept for that id while a sort, page or signal change refetches the entries.
    const filterKey = employeeId.toLowerCase()
    const foundEmployeeName = useMemo(() => {
        if (!filterKey) return null
        return employees.data?.rows.find(r => r.employeeId?.toLowerCase() === filterKey)?.employeeName
            ?? entries.data?.rows.find(r => r.employeeId?.toLowerCase() === filterKey)?.appliedBy
            ?? null
    }, [filterKey, employees.data, entries.data])
    const [knownEmployee, setKnownEmployee] = useState<{ id: string; name: string } | null>(null)
    if (foundEmployeeName && (knownEmployee?.id !== filterKey || knownEmployee.name !== foundEmployeeName)) {
        setKnownEmployee({ id: filterKey, name: foundEmployeeName })
    }
    const filteredEmployeeName = foundEmployeeName ?? (knownEmployee?.id === filterKey ? knownEmployee.name : null)

    const meta = overview.data?.meta ?? entries.data?.meta ?? employees.data?.meta ?? autoRegs.data?.meta ?? null
    const error = overview.error ?? entries.error ?? employees.error ?? autoRegs.error

    const dataset = tab === 'entries' ? 'entries' : tab === 'employees' ? 'employees'
        : tab === 'auto' ? 'auto-registrations' : 'stores'

    return (
        <div className="page">
            <div className="page-header">
                <h1>🪙 Adventure Bucks audit</h1>
                <p style={{ color: 'var(--color-text-secondary)', marginTop: 4 }}>
                    Loyalty credits that don't look like ordinary earning — oversized entries, duplicate
                    and orphaned credits, rate anomalies, ad-hoc adjustments, credits applied long
                    after the sale, and same-day churn.
                </p>
            </div>

            <FilterBar
                config={config}
                allHistory={allHistory}
                from={from} to={to}
                storeId={storeId}
                signals={signals}
                signalCounts={overview.data?.signalCounts}
                perEntry={perEntry} daily={daily} selfCredit={selfCredit} retroDays={retroDays}
                canExport={canExport}
                exportQuery={tab === 'entries' ? entriesQuery.toString() : query}
                dataset={dataset}
                token={token}
                onPatch={patch}
            />

            {employeeId && (
                <div className="ab-callout">
                    <span>🔎</span>
                    <span>
                        Showing only entries applied by{' '}
                        <strong>{filteredEmployeeName ?? 'the selected employee'}</strong>.{' '}
                        <button className="ab-row-link" onClick={() => patch({ employeeId: null })}>Clear this filter</button>
                    </span>
                </div>
            )}

            <div className="admin-subtabs">
                <TabButton id="overview" active={tab} onClick={setTab} label="Overview" />
                <TabButton id="entries" active={tab} onClick={setTab} label="Entries"
                    count={overview.data?.totals.flaggedEntries} />
                <TabButton id="employees" active={tab} onClick={setTab} label="Employees"
                    count={overview.data?.totals.employees} />
                <TabButton id="auto" active={tab} onClick={setTab} label="Auto-registrations"
                    count={overview.data?.totals.autoRegEntries} />
            </div>

            {error && (
                <div className="data-table-wrapper">
                    <div className="ab-empty">
                        <div style={{ color: 'var(--color-error)', fontWeight: 600 }}>{errorMessage(error)}</div>
                        <div style={{ marginTop: 'var(--space-2)' }}>
                            The audit reads the legacy loyalty log directly. If this keeps happening, check the
                            active database connection in System Admin.
                        </div>
                    </div>
                </div>
            )}

            {!error && tab === 'overview' && (
                <OverviewTab data={overview.data} loading={overview.isLoading}
                    onPickStore={id => patch({ storeId: id ?? 'unassigned' })}
                    onOpenEntries={() => setTab('entries')} />
            )}
            {!error && tab === 'entries' && (
                <EntriesTab data={entries.data} loading={entries.isLoading}
                    sort={sort} page={page} signalDefs={config?.signals ?? []}
                    onSort={s => patch({ sort: s })}
                    onPage={p => patch({ page: String(p) }, false)} />
            )}
            {!error && tab === 'employees' && (
                <EmployeesTab data={employees.data} loading={employees.isLoading}
                    onDrillDown={id => { patch({ employeeId: id }); setTab('entries') }} />
            )}
            {!error && tab === 'auto' && (
                <AutoRegTab data={autoRegs.data} loading={autoRegs.isLoading} />
            )}

            {meta && (
                <div className="ab-meta-line">
                    {meta.allHistory ? 'All history' : `${meta.from} → ${meta.to}`}
                    {' · '}entry ≥ {money(meta.perEntryThreshold)}, day ≥ {money(meta.dailyThreshold)},
                    self-credit ≥ {money(meta.selfCreditThreshold)},
                    credited ≥ {count(meta.retroDaysThreshold)} days after the sale
                    {' · '}built {dateTime(meta.builtAt)} in {(meta.buildMs / 1000).toFixed(1)}s
                    {' · '}results are cached for 10 minutes
                </div>
            )}
        </div>
    )
}

function TabButton({ id, active, onClick, label, count: n }: {
    id: Tab; active: Tab; onClick: (t: Tab) => void; label: string; count?: number
}) {
    return (
        <button className={`admin-subtab${active === id ? ' active' : ''}`} onClick={() => onClick(id)}>
            {label}
            {n != null && n > 0 && (
                <span className="tab-count" style={{ background: 'var(--color-surface-hover)', color: 'var(--color-text-secondary)' }}>
                    {count(n)}
                </span>
            )}
        </button>
    )
}

// ── Filter bar ────────────────────────────────────────────────────────

function FilterBar({
    config, allHistory, from, to, storeId, signals, signalCounts,
    perEntry, daily, selfCredit, retroDays, canExport, exportQuery, dataset, token, onPatch,
}: {
    config?: AbConfig
    allHistory: boolean; from: string; to: string; storeId: string; signals: string
    signalCounts?: Record<string, number>
    perEntry: string; daily: string; selfCredit: string; retroDays: string
    canExport: boolean; exportQuery: string; dataset: string; token: string | null
    onPatch: (changes: Record<string, string | null>, resetPage?: boolean) => void
}) {
    const [openPanel, setOpenPanel] = useState<'signals' | 'thresholds' | null>(null)
    const [exporting, setExporting] = useState(false)
    const [exportError, setExportError] = useState<string | null>(null)
    const barRef = useRef<HTMLDivElement>(null)

    // Popovers are transient view state, not a dialog — outside-click closes them.
    useEffect(() => {
        if (!openPanel) return
        const close = (e: MouseEvent) => {
            if (!barRef.current?.contains(e.target as Node)) setOpenPanel(null)
        }
        document.addEventListener('mousedown', close)
        return () => document.removeEventListener('mousedown', close)
    }, [openPanel])

    const openStores = useMemo(
        () => (config?.stores ?? []).filter(s => !s.isClosed), [config])
    const closedStores = useMemo(
        () => (config?.stores ?? []).filter(s => s.isClosed), [config])

    const allSignalKeys = useMemo(() => (config?.signals ?? []).map(s => s.key), [config])
    const selected = useMemo(
        () => new Set(signals ? signals.split(',').filter(Boolean) : allSignalKeys),
        [signals, allSignalKeys])

    const toggleSignal = (key: string) => {
        const next = new Set(selected)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        // Every signal selected is the same query as none selected; keep the URL clean.
        if (next.size === 0 || next.size === allSignalKeys.length) onPatch({ signals: null })
        else onPatch({ signals: allSignalKeys.filter(k => next.has(k)).join(',') })
    }

    const download = async () => {
        if (!token) return
        setExporting(true)
        setExportError(null)
        try {
            const res = await fetch(`${API_BASE}/api/loyalty-audit/export.csv?${exportQuery}&dataset=${dataset}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            if (!res.ok) throw new Error(await res.text())
            const blob = await res.blob()
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `adventure-bucks-${dataset}.csv`
            document.body.appendChild(link)
            link.click()
            link.remove()
            URL.revokeObjectURL(url)
        } catch (err) {
            // A silently-nothing-happens download button reads as a broken page. Say so.
            setExportError(errorMessage(err))
        } finally {
            setExporting(false)
        }
    }

    const signalLabel = selected.size === allSignalKeys.length
        ? 'All signals'
        : `${selected.size} of ${allSignalKeys.length} signals`

    const thresholdsChanged = !!(perEntry || daily || selfCredit || retroDays)

    // A store picked through a link can arrive in a different letter case from the picker's ids (the
    // dashboards hand out the store mirror's), or be missing from a partial list. Match without case,
    // and name an unlisted store rather than let the picker fall back to showing My stores over one
    // store's figures.
    const storeKey = storeId.toLowerCase()
    const special = storeKey === '' || storeKey === 'all' || storeKey === 'unassigned'
    const listedStore = special ? undefined : config?.stores.find(s => s.id.toLowerCase() === storeKey)
    const storeValue = special ? storeKey : listedStore?.id ?? storeId
    const unlistedStore = !!config && !special && !listedStore

    return (
        <div className="ab-filters" ref={barRef}>
            <div className="ab-field">
                <label>Window</label>
                {allHistory ? (
                    <span style={{ fontSize: 'var(--text-sm)' }}>All history</span>
                ) : (
                    <DateRangePicker
                        startDate={from}
                        endDate={to}
                        onApply={(start, end) => onPatch({ from: start, to: end, window: null })}
                    />
                )}
                <button
                    className={`ab-btn${allHistory ? ' active' : ''}`}
                    onClick={() => onPatch({ window: allHistory ? null : 'all' })}
                    title="All history is a full scan of the loyalty log and takes several seconds"
                >
                    {allHistory ? 'Use a date range' : 'All history'}
                </button>
            </div>

            <div className="ab-field">
                <label htmlFor="ab-store">Store</label>
                <select id="ab-store" className="ab-select" value={storeValue}
                    onChange={e => onPatch({ storeId: e.target.value })}>
                    {unlistedStore && <option value={storeId}>Store from the link (not in this list)</option>}
                    <option value="">My stores</option>
                    {config?.canViewChainWide && <option value="all">All stores</option>}
                    {/* Closed stores still hold flagged history — Red Deer alone has 52,772 loyalty
                        entries — so they stay selectable, just penned off from the trading ones. */}
                    {openStores.length > 0 && (
                        <optgroup label="Stores">
                            {openStores.map(s => <StoreOption key={s.id} store={s} />)}
                        </optgroup>
                    )}
                    {closedStores.length > 0 && (
                        <optgroup label="Closed">
                            {closedStores.map(s => <StoreOption key={s.id} store={s} />)}
                        </optgroup>
                    )}
                    {config?.canViewUnassigned && <option value="unassigned">(no store — eStore/HO)</option>}
                </select>
                {config && !config.storesFromLegacy && (
                    <span className="ab-note" style={{ color: 'var(--color-warning)' }}
                        title="The legacy database was unreachable, so this list came from the store mirror. It can be missing stores the audit will still return rows for — use All stores to see everything.">
                        partial list
                    </span>
                )}
            </div>

            <div className="ab-popover-wrap">
                <button className={`ab-btn${selected.size !== allSignalKeys.length ? ' active' : ''}`}
                    onClick={() => setOpenPanel(openPanel === 'signals' ? null : 'signals')}>
                    🚩 {signalLabel} ▾
                </button>
                {openPanel === 'signals' && (
                    <div className="ab-popover">
                        <h4>Signals</h4>
                        <p className="ab-hint">
                            An entry is listed if it carries any selected signal. In practice the signals
                            barely overlap, so turning one off removes almost exactly its own rows.
                        </p>
                        {(config?.signals ?? []).map(s => (
                            <label key={s.key} className="ab-signal-option">
                                <input type="checkbox" checked={selected.has(s.key)} onChange={() => toggleSignal(s.key)} />
                                <span style={{ flex: 1 }}>
                                    <span className="ab-signal-label">{s.label}</span>
                                    <span className="ab-signal-desc" style={{ display: 'block' }}>{s.description}</span>
                                </span>
                                {signalCounts?.[s.key] != null && (
                                    <span className="ab-signal-count">{count(signalCounts[s.key])}</span>
                                )}
                            </label>
                        ))}
                        <div className="ab-popover-actions">
                            <button className="ab-btn" onClick={() => onPatch({ signals: null })}>Select all</button>
                            <button className="ab-btn"
                                onClick={() => onPatch({ signals: allSignalKeys.filter(k => k !== 'sameDayChurn').join(',') })}>
                                Hide same-day churn
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="ab-popover-wrap">
                <button className={`ab-btn${thresholdsChanged ? ' active' : ''}`}
                    onClick={() => setOpenPanel(openPanel === 'thresholds' ? null : 'thresholds')}>
                    ⚙ Thresholds ▾
                </button>
                {openPanel === 'thresholds' && (
                    <div className="ab-popover">
                        <h4>Thresholds</h4>
                        <p className="ab-hint">
                            Whole numbers. Changing one rebuilds the audit from the loyalty log, so give it
                            a moment.
                        </p>
                        <ThresholdField
                            label="Single entry credits at least"
                            desc="Flags one oversized credit on its own."
                            unit="$"
                            value={perEntry} fallback={config?.defaults.perEntryThreshold}
                            onCommit={v => onPatch({ per: v })} />
                        <ThresholdField
                            label="One customer's day totals at least"
                            desc="Summed across every store, so a burst split between locations still trips it."
                            unit="$"
                            value={daily} fallback={config?.defaults.dailyThreshold}
                            onCommit={v => onPatch({ daily: v })} />
                        <ThresholdField
                            label="Self-credit flags at"
                            desc="An employee crediting a same-named customer. Ad-hoc entries always flag, whatever the amount."
                            unit="$"
                            value={selfCredit} fallback={config?.defaults.selfCreditThreshold}
                            onCommit={v => onPatch({ self: v })} />
                        <ThresholdField
                            label="Credited this long after the sale"
                            desc="Days between the sale and the credit. Ad-hoc entries and orphans have no sale to measure from, so they never trip this."
                            unit="days"
                            value={retroDays} fallback={config?.defaults.retroDaysThreshold}
                            onCommit={v => onPatch({ retro: v })} />
                        <div className="ab-popover-actions">
                            <button className="ab-btn"
                                onClick={() => onPatch({ per: null, daily: null, self: null, retro: null })}>
                                Reset to defaults
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="ab-spacer" />

            {canExport && (
                <button className="ab-btn" onClick={download} disabled={exporting}
                    title={exportError ?? undefined}>
                    {exporting ? 'Preparing…' : '⤓ Export CSV'}
                </button>
            )}

            {exportError && (
                <span className="ab-note" style={{ color: 'var(--color-error)', flexBasis: '100%' }}>
                    Export failed — {exportError}
                </span>
            )}
        </div>
    )
}

/**
 * The store number is the label people actually use ("99 is the DC"), but the mirror fallback can
 * hand back an empty one — so the name has to stand alone rather than leaving a bare dash.
 */
function StoreOption({ store }: { store: AbStoreOption }) {
    const number = store.storeNumber?.trim()
    return <option value={store.id}>{number ? `${number} — ${store.name}` : store.name}</option>
}

function ThresholdField({ label, desc, unit, value, fallback, onCommit }: {
    label: string; desc: string; unit: '$' | 'days'
    value: string; fallback?: number; onCommit: (v: string | null) => void
}) {
    const [draft, setDraft] = useState(value)
    useEffect(() => setDraft(value), [value])

    const commit = () => {
        const trimmed = draft.trim()
        if (trimmed === value) return
        onCommit(trimmed === '' ? null : trimmed)
    }

    return (
        <div className="ab-threshold-row">
            <span style={{ flex: 1 }}>
                <span className="ab-threshold-label">{label}</span>
                <span className="ab-threshold-desc" style={{ display: 'block' }}>{desc}</span>
            </span>
            {/* Dollars and days sit in the same list, so each field says which it is — otherwise
                "30" against "Credited this long after the sale" reads as $30. */}
            <span className="ab-threshold-input">
                {unit === '$' && <span className="ab-threshold-unit">$</span>}
                <input className="ab-input" type="number" min={1} step={1}
                    placeholder={fallback != null ? String(fallback) : ''}
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }} />
                {unit === 'days' && <span className="ab-threshold-unit">days</span>}
            </span>
        </div>
    )
}

// ── Overview tab ──────────────────────────────────────────────────────

function OverviewTab({ data, loading, onPickStore, onOpenEntries }: {
    data?: AbOverview; loading: boolean
    onPickStore: (storeId: string | null) => void
    onOpenEntries: () => void
}) {
    if (loading && !data) return <Loading />
    if (!data) return null

    const t = data.totals

    return (
        <>
            <div className="stats-grid" style={{ marginBottom: 'var(--space-4)' }}>
                <Stat label="Flagged entries" value={count(t.flaggedEntries)} icon="🚩" tone="amber" />
                <Stat label="Credited on flagged entries" value={money(t.totalCredited)} icon="🪙" tone="purple" />
                <Stat label="Customers involved" value={count(t.customers)} icon="👤" tone="blue" />
                <Stat label="Employees involved" value={count(t.employees)} icon="🧑‍💼" tone="green" />
            </div>

            <div className="data-table-wrapper">
                <div className="data-table-toolbar">
                    <h3>By store</h3>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                        Pick a store to narrow every tab to it
                    </span>
                </div>
                <div className="ab-table-scroll">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Store</th>
                                <th className="ab-num">Flagged</th>
                                <th className="ab-num">Customers</th>
                                <th className="ab-num">Credited</th>
                                <th className="ab-num">Auto-reg entries</th>
                                <th className="ab-num">Auto-reg credited</th>
                                <th>Range</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.stores.map(s => (
                                <tr key={s.storeId ?? 'none'}>
                                    <td>
                                        <button className="ab-row-link"
                                            onClick={() => { onPickStore(s.storeId); onOpenEntries() }}>
                                            {s.storeNumber ? `${s.storeNumber} — ` : ''}{s.storeName}
                                        </button>
                                    </td>
                                    <td className="ab-num">{count(s.flaggedEntries)}</td>
                                    <td className="ab-num">{count(s.customers)}</td>
                                    <td className="ab-num ab-money">{money(s.totalCredited)}</td>
                                    <td className="ab-num">{count(s.autoRegEntries)}</td>
                                    <td className="ab-num">{money(s.autoRegCredited)}</td>
                                    <td className="ab-note">{s.firstDate} → {s.lastDate}</td>
                                </tr>
                            ))}
                            {data.stores.length === 0 && (
                                <tr><td colSpan={7} className="ab-empty">Nothing flagged in this window.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="ab-callout" style={{ marginTop: 'var(--space-4)' }}>
                <span>ℹ️</span>
                <span>
                    Totals are computed chain-wide and then narrowed to the stores you can see, because a
                    customer's daily total sums their credits across every store. That is why a row can say
                    "day total includes other-store activity" without the other store appearing here.
                </span>
            </div>
        </>
    )
}

function Stat({ label, value, icon, tone }: { label: string; value: string; icon: string; tone: string }) {
    return (
        <div className="stat-card">
            <div className="stat-card-header">
                <span className="stat-label">{label}</span>
                <span className={`stat-icon ${tone}`}>{icon}</span>
            </div>
            <div className="stat-value">{value}</div>
        </div>
    )
}

// ── Entries tab ───────────────────────────────────────────────────────

function EntriesTab({ data, loading, sort, page, signalDefs, onSort, onPage }: {
    data?: AbEntries; loading: boolean; sort: string; page: number
    signalDefs: AbSignal[]
    onSort: (s: string) => void; onPage: (p: number) => void
}) {
    const [openId, setOpenId] = useState<string | null>(null)
    const labels = useMemo(
        () => Object.fromEntries(signalDefs.map(s => [s.key, s.label])), [signalDefs])

    if (loading && !data) return <Loading />
    if (!data) return null

    const pages = Math.max(Math.ceil(data.total / data.pageSize), 1)

    return (
        <div className="data-table-wrapper">
            <div className="data-table-toolbar">
                <h3>{count(data.total)} flagged {data.total === 1 ? 'entry' : 'entries'}</h3>
                <div className="ab-field">
                    <label htmlFor="ab-sort">Sort</label>
                    <select id="ab-sort" className="ab-select" value={sort} onChange={e => onSort(e.target.value)}>
                        {SORTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                </div>
            </div>

            <div className="ab-table-scroll">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Store</th>
                            <th>Customer</th>
                            <th>Applied by</th>
                            <th className="ab-num">Entry</th>
                            <th className="ab-num">Day</th>
                            <th>Signals</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.map(e => (
                            <EntryRow key={e.entryId} entry={e} labels={labels}
                                open={openId === e.entryId}
                                onToggle={() => setOpenId(openId === e.entryId ? null : e.entryId)} />
                        ))}
                        {data.rows.length === 0 && (
                            <tr><td colSpan={7} className="ab-empty">
                                Nothing matches these filters. Try widening the window or re-enabling a signal.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {pages > 1 && (
                <div className="ab-pager">
                    <span>Page {page} of {count(pages)}</span>
                    <span style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <button className="ab-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>← Previous</button>
                        <button className="ab-btn" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next →</button>
                    </span>
                </div>
            )}
        </div>
    )
}

function EntryRow({ entry: e, labels, open, onToggle }: {
    entry: AbEntry; labels: Record<string, string>; open: boolean; onToggle: () => void
}) {
    return (
        <>
            <tr className={`ab-expandable${open ? ' ab-open' : ''}`} onClick={onToggle}>
                <td>
                    {e.date}
                    <div className="ab-note">{new Date(e.creditedAt).toLocaleTimeString()}</div>
                </td>
                <td>
                    {e.storeNumber ?? '—'}
                    <div className="ab-note">{e.storeName}</div>
                </td>
                <td>
                    {e.customerName}
                    <div className="ab-note">{e.loyaltyNumber ?? 'no loyalty number'}</div>
                </td>
                <td>
                    {e.appliedBy}
                    <div className="ab-note">{e.logType.replace(/_/g, ' ').toLowerCase()}</div>
                </td>
                <td className="ab-num ab-money">{money(e.entryCredited)}</td>
                <td className="ab-num">{money(e.dayCredited)}</td>
                <td>
                    <div className="ab-chips">
                        {e.signals.map(s => (
                            <span key={s} className={`ab-chip sev-${SEVERITY[s] ?? 'low'}`}>{labels[s] ?? s}</span>
                        ))}
                    </div>
                </td>
            </tr>
            {open && (
                <tr className="ab-detail">
                    <td colSpan={7}>
                        <dl className="ab-detail-grid">
                            <Detail term="Credited at" value={dateTime(e.creditedAt)} />
                            <Detail term="Customer email" value={e.email ?? '—'} />
                            <Detail term="Entry net" value={money(e.entryNet)} />
                            <Detail term="Item lines" value={count(e.itemLines)} />
                            <Detail term="Day redeemed" value={money(e.dayRedeemed)} />
                            <Detail term="Manual credit entries that day" value={count(e.dayManualCreditEntries)} />
                            <Detail term="Stores active that day" value={count(e.dayStoreCount)} />
                            <Detail term="Sale time" value={dateTime(e.saleTime)} />
                            <Detail term="Sale subtotal" value={money(e.saleSubTotal)} />
                            <Detail term="eStore order" value={e.eStoreOrderNumber != null ? String(e.eStoreOrderNumber) : '—'} />
                            <Detail term="Credited after sale" value={e.retroDays != null ? `${e.retroDays} day(s)` : '—'} />
                            <Detail term="Stamped earn rate" value={rate(e.maxEarnRate)} />
                            <Detail term="Note class" value={e.noteClass} />
                            <Detail term="Entry id" value={<span className="ab-mono">{e.entryId}</span>} />
                            <Detail term="Transaction id" value={<span className="ab-mono">{e.relatedTransactionId ?? '—'}</span>} />
                        </dl>

                        {e.notes.length > 0 && (
                            <div style={{ marginTop: 'var(--space-3)' }}>
                                <div className="ab-chips">
                                    {e.notes.map(n => <span key={n} className="ab-chip">{n}</span>)}
                                </div>
                            </div>
                        )}

                        {e.noteText && (
                            <div style={{ marginTop: 'var(--space-3)' }}>
                                <div className="ab-note" style={{ marginBottom: 2 }}>Till note</div>
                                <div style={{ fontSize: 'var(--text-sm)' }}>{e.noteText}</div>
                            </div>
                        )}
                    </td>
                </tr>
            )}
        </>
    )
}

function Detail({ term, value }: { term: string; value: ReactNode }) {
    return (
        <div>
            <dt>{term}</dt>
            <dd>{value}</dd>
        </div>
    )
}

// ── Employees tab ─────────────────────────────────────────────────────

function EmployeesTab({ data, loading, onDrillDown }: {
    data?: AbEmployees; loading: boolean; onDrillDown: (employeeId: string) => void
}) {
    const [openKey, setOpenKey] = useState<string | null>(null)

    if (loading && !data) return <Loading />
    if (!data) return null

    return (
        <div className="data-table-wrapper">
            <div className="data-table-toolbar">
                <h3>{count(data.total)} employee {data.total === 1 ? 'record' : 'records'}</h3>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                    Expand a row for that person's day-by-day credit totals, computed over every entry —
                    not only flagged ones
                </span>
            </div>

            <div className="ab-table-scroll">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Employee</th>
                            <th>Store</th>
                            <th className="ab-num">Flagged</th>
                            <th className="ab-num">Customers</th>
                            <th className="ab-num">Credited</th>
                            <th className="ab-num">Ad-hoc</th>
                            <th className="ab-num">Self-credit</th>
                            <th className="ab-num">Monitor days</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.map(r => {
                            const key = `${r.storeId}|${r.employeeId}`
                            const open = openKey === key
                            return (
                                <Fragment key={key}>
                                    <tr className={`ab-expandable${open ? ' ab-open' : ''}`}
                                        onClick={() => setOpenKey(open ? null : key)}>
                                        <td>
                                            {r.employeeName}
                                            {r.departed && <span className="ab-departed">departed</span>}
                                        </td>
                                        <td>
                                            {r.storeNumber ?? '—'}
                                            <div className="ab-note">{r.storeName}</div>
                                        </td>
                                        <td className="ab-num">{count(r.flaggedEntries)}</td>
                                        <td className="ab-num">{count(r.customers)}</td>
                                        <td className="ab-num ab-money">{money(r.totalCredited)}</td>
                                        <td className="ab-num">{count(r.adHocEntries)}</td>
                                        <td className="ab-num">
                                            {r.selfCreditEntries > 0
                                                ? <span className="ab-chip sev-high">{count(r.selfCreditEntries)}</span>
                                                : '—'}
                                        </td>
                                        <td className="ab-num">
                                            {r.monitorDayCount > 0 ? count(r.monitorDayCount) : '—'}
                                            {r.monitorOrphans > 0 && (
                                                <div className="ab-note" style={{ color: 'var(--color-error)' }}>
                                                    {count(r.monitorOrphans)} orphan
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {r.employeeId && r.flaggedEntries > 0 && (
                                                <button className="ab-row-link"
                                                    onClick={ev => { ev.stopPropagation(); onDrillDown(r.employeeId!) }}>
                                                    View entries →
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                    {open && (
                                        <tr className="ab-detail">
                                            <td colSpan={9}>
                                                {r.monitorDays.length === 0 ? (
                                                    <div className="ab-note">
                                                        No day trips the monitor — this person's flagged entries were caught
                                                        by the customer-level thresholds, not by their own daily volume.
                                                    </div>
                                                ) : (
                                                    <table className="data-table" style={{ background: 'transparent' }}>
                                                        <thead>
                                                            <tr>
                                                                <th>Date</th>
                                                                <th className="ab-num">Manual credited</th>
                                                                <th className="ab-num">Customers</th>
                                                                <th className="ab-num">Manual retro</th>
                                                                <th className="ab-num">Ad-hoc</th>
                                                                <th className="ab-num">Orphans</th>
                                                                <th className="ab-num">Self-credit</th>
                                                                <th className="ab-num">Auto-reg credited</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {r.monitorDays.map(d => (
                                                                <tr key={d.date}>
                                                                    <td>{d.date}</td>
                                                                    <td className="ab-num ab-money">{money(d.manualCredited)}</td>
                                                                    <td className="ab-num">{count(d.customersCredited)}</td>
                                                                    <td className="ab-num">{count(d.manualRetroEntries)}</td>
                                                                    <td className="ab-num">{count(d.adHocEntries)}</td>
                                                                    <td className="ab-num">{d.orphanEntries > 0 ? count(d.orphanEntries) : '—'}</td>
                                                                    <td className="ab-num">{d.selfCreditEntries > 0 ? count(d.selfCreditEntries) : '—'}</td>
                                                                    <td className="ab-num">{money(d.autoRegCredited)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            )
                        })}
                        {data.rows.length === 0 && (
                            <tr><td colSpan={9} className="ab-empty">No employee activity in this window.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

// ── Auto-registration tab ─────────────────────────────────────────────

function AutoRegTab({ data, loading }: { data?: AbAutoRegs; loading: boolean }) {
    if (loading && !data) return <Loading />
    if (!data) return null

    return (
        <>
            <div className="ab-callout">
                <span>ℹ️</span>
                <span>
                    Registering a customer legitimately back-fills their whole purchase history in one
                    burst, which is why these are kept out of the main queue. The column worth reading is
                    <strong> burst days</strong>: one is normal, several for the same customer is not.
                </span>
            </div>

            <div className="data-table-wrapper">
                <div className="data-table-toolbar">
                    <h3>{count(data.total)} registration {data.total === 1 ? 'burst' : 'bursts'}</h3>
                </div>
                <div className="ab-table-scroll">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Store</th>
                                <th>Customer</th>
                                <th>Registered by</th>
                                <th className="ab-num">Entries</th>
                                <th className="ab-num">Credited</th>
                                <th className="ab-num">Burst days</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map(r => (
                                <tr key={`${r.storeId}|${r.date}|${r.loyaltyNumber}`}>
                                    <td>{r.date}</td>
                                    <td>
                                        {r.storeNumber ?? '—'}
                                        <div className="ab-note">{r.storeName}</div>
                                    </td>
                                    <td>
                                        {r.customerName}
                                        <div className="ab-note">{r.loyaltyNumber ?? 'no loyalty number'}</div>
                                    </td>
                                    <td>{r.registeredBy}</td>
                                    <td className="ab-num">{count(r.entries)}</td>
                                    <td className="ab-num ab-money">{money(r.totalCredited)}</td>
                                    <td className="ab-num">
                                        {r.burstDaysForCustomer > 1
                                            ? <span className="ab-chip sev-medium">{count(r.burstDaysForCustomer)}</span>
                                            : count(r.burstDaysForCustomer)}
                                    </td>
                                </tr>
                            ))}
                            {data.rows.length === 0 && (
                                <tr><td colSpan={7} className="ab-empty">No registration bursts in this window.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    )
}

function Loading() {
    return (
        <div className="data-table-wrapper">
            <div className="ab-empty">Building the audit from the loyalty log…</div>
        </div>
    )
}
